import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { formatDisplayDate, formatDisplayDateTime } from "utils/dateFormat";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { useInvoicePrintTheme } from "hooks/useInvoicePrintTheme";
import { getInvoicePrintPageStyle } from "utils/invoicePrintTheme";
import { formatMoney } from "utils/utils";
import { paymentReceipts } from "./invoiceReceipts";
import InvoiceTemplate from "./InvoiceTemplate";
import { refreshInvoiceVerificationCaches } from "utils/invoiceVerificationQueries";
import ModuleInvoiceSourceChip from "components/ModuleInvoice/ModuleInvoiceSourceChip";
import { isModuleInvoice } from "components/ModuleInvoice/moduleLinks";
import { invalidateModuleFinance } from "components/ModulePayments/moduleFinanceQueries";

const PAYMENT_CONFLICT = 409;

/** A module payment is decided at the ledger revision it was listed at, so a later edit is refused. */
const decisionParams = (item) =>
  item?.revision != null ? { params: { expectedRevision: item.revision } } : undefined;

const InvoiceVerificationRequests = () => {
  const queryClient = useQueryClient();
  const [verifyDialog, setVerifyDialog] = useState({ open: false, item: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, item: null });
  const [rejectFeedback, setRejectFeedback] = useState("");
  const [detailDialog, setDetailDialog] = useState({ open: false, item: null });
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [invoicePreviewData, setInvoicePreviewData] = useState(null);
  // Payments already verified or rejected in this session, so their row cannot be actioned twice
  // while the pending list is still refetching.
  const [settledPaymentIds, setSettledPaymentIds] = useState(() => new Set());
  const previewRef = useRef(null);
  const { darkPrint } = useInvoicePrintTheme();
  const printPreview = useReactToPrint({
    contentRef: previewRef,
    pageStyle: getInvoicePrintPageStyle(darkPrint),
    onAfterPrint: () => setPreviewInvoice(null),
  });

  useEffect(() => {
    if (previewInvoice) printPreview();
  }, [previewInvoice]);

  const {
    data: requests = [],
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["invoice-verification-pending"],
    queryFn: () =>
      axios.get(`${API_URL}/invoice-verification/pending`).then((r) => r.data?.data ?? []),
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  /**
   * Awaited rather than fired and forgotten, and the id is remembered: the row keeps its stale
   * PENDING copy until the refetch lands, so between the toast and the list catching up its Verify
   * and Reject buttons were live again on a payment the server has already moved on.
   */
  const settle = async (item, message) => {
    setSettledPaymentIds((ids) => new Set(ids).add(item.paymentId));
    toast.success(message);
    await Promise.all([
      refetch(),
      refreshInvoiceVerificationCaches(queryClient),
      isModuleInvoice(item)
        ? invalidateModuleFinance(queryClient, item.moduleType, item.moduleId, item.clientId)
        : null,
    ]);
  };

  // A conflict means the payment moved on since it was listed; reload so the row shows what it is now.
  const failDecision = (fallback) => (error) => {
    toast.error(error?.response?.data?.message || fallback);
    if (error?.response?.status === PAYMENT_CONFLICT) refetch();
  };

  const verifyMutation = useMutation({
    mutationFn: (item) =>
      axios.post(
        `${API_URL}/invoice-verification/payments/${item.paymentId}/verify`,
        undefined,
        decisionParams(item)
      ),
    onSuccess: (_data, item) => {
      setVerifyDialog({ open: false, item: null });
      return settle(item, "Payment verified");
    },
    onError: failDecision("Failed to verify payment"),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ item, feedback }) =>
      axios.post(
        `${API_URL}/invoice-verification/payments/${item.paymentId}/reject`,
        { feedback },
        decisionParams(item)
      ),
    onSuccess: (_data, { item }) => {
      setRejectDialog({ open: false, item: null });
      setRejectFeedback("");
      return settle(item, "Payment rejected");
    },
    onError: failDecision("Failed to reject payment"),
  });

  const handleInvoicePreview = async (invoiceId) => {
    if (!invoiceId) return;
    try {
      const response = await axios.get(`${API_URL}/invoice/${invoiceId}`);
      setInvoicePreviewData(response.data?.data);
      setPreviewInvoice(response.data?.data);
    } catch {
      toast.error("Could not load invoice preview");
    }
  };

  return (
    <Box>
      <TableContainer className="border border-app-border rounded-lg">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Invoice</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Payment date</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Sent by</TableCell>
              <TableCell>Sent at</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((item) => (
              <TableRow key={item.paymentId}>
                <TableCell>{item.clientName || "-"}</TableCell>
                <TableCell>
                  <Stack spacing={0.25}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{item.invoiceTitle || "-"}</Typography>
                      <ModuleInvoiceSourceChip invoice={item} />
                    </Stack>
                    {item.invoiceId && (
                      <Typography variant="caption" className="text-app-text-muted">
                        #{item.invoiceId}
                        {item.invoiceTotal != null && ` · ${formatMoney(item.invoiceTotal)}`}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">{formatMoney(item.amount || 0)}</TableCell>
                <TableCell>{formatDisplayDate(item.paymentDate)}</TableCell>
                <TableCell>{item.paymentMethod || "-"}</TableCell>
                <TableCell>{item.sentByName || "-"}</TableCell>
                <TableCell>{formatDisplayDateTime(item.sentAt)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon fontSize="small" />}
                      onClick={() => setDetailDialog({ open: true, item })}
                    >
                      Details
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={settledPaymentIds.has(item.paymentId)}
                      onClick={() => setVerifyDialog({ open: true, item })}
                    >
                      Verify
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={settledPaymentIds.has(item.paymentId)}
                      onClick={() => setRejectDialog({ open: true, item })}
                    >
                      Reject
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!isFetching && requests.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" className="text-app-text-muted">
                  No payments pending verification.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Payment details</DialogTitle>
        <DialogContent className="space-y-3">
          {detailDialog.item && (
            <>
              <Typography variant="body2">
                <strong>Client:</strong> {detailDialog.item.clientName || "-"}
              </Typography>
              <Typography variant="body2">
                <strong>Invoice:</strong> {detailDialog.item.invoiceTitle || "-"}
                {detailDialog.item.invoiceId ? ` (#${detailDialog.item.invoiceId})` : ""}
              </Typography>
              <Typography variant="body2">
                <strong>Amount:</strong> {formatMoney(detailDialog.item.amount || 0)}
              </Typography>
              <Typography variant="body2">
                <strong>Payment date:</strong> {formatDisplayDate(detailDialog.item.paymentDate)}
              </Typography>
              <Typography variant="body2">
                <strong>Method:</strong> {detailDialog.item.paymentMethod || "-"}
              </Typography>
              {detailDialog.item.note && (
                <Typography variant="body2">
                  <strong>Note:</strong> {detailDialog.item.note}
                </Typography>
              )}
              {detailDialog.item.status && (
                <Chip size="small" label={detailDialog.item.status} color="warning" />
              )}
              {paymentReceipts(detailDialog.item).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" className="mb-1">
                    Receipts
                  </Typography>
                  <Stack spacing={0.5}>
                    {paymentReceipts(detailDialog.item).map((url) => (
                      <Link key={url} href={url} target="_blank" rel="noopener noreferrer">
                        View receipt
                      </Link>
                    ))}
                  </Stack>
                </Box>
              )}
              {detailDialog.item.invoiceId && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleInvoicePreview(detailDialog.item.invoiceId)}
                >
                  Preview full invoice
                </Button>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, item: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={verifyDialog.open}
        onClose={() => setVerifyDialog({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Verify Payment</DialogTitle>
        <DialogContent>
          <Typography>
            Verify payment of {formatMoney(verifyDialog.item?.amount || 0)} for{" "}
            {verifyDialog.item?.clientName} ({verifyDialog.item?.invoiceTitle || "invoice"})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog({ open: false, item: null })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => verifyMutation.mutate(verifyDialog.item)}
            disabled={verifyMutation.isLoading}
          >
            {verifyMutation.isLoading ? "Verifying…" : "Verify"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Payment</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for rejection"
            multiline
            rows={3}
            fullWidth
            required
            value={rejectFeedback}
            onChange={(e) => setRejectFeedback(e.target.value)}
            sx={{ mt: 1 }}
            error={rejectFeedback.trim() === ""}
            helperText={rejectFeedback.trim() === "" ? "Required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, item: null })}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={rejectFeedback.trim() === "" || rejectMutation.isLoading}
            onClick={() =>
              rejectMutation.mutate({
                item: rejectDialog.item,
                feedback: rejectFeedback.trim(),
              })
            }
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <InvoiceTemplate
        printData={invoicePreviewData || previewInvoice || {}}
        contentRef={previewRef}
      />
    </Box>
  );
};

export default InvoiceVerificationRequests;
